import { useState, type ReactNode } from "react";
import { Link } from "wouter";
import { AlertTriangle, CheckCircle2, CircleHelp, CircleSlash, Clock, Hand, Loader2, Pause, Play, RefreshCw, ShieldCheck, ShieldX, Undo2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Chip } from "./chip";
import { fmtDateTime, humanizeCheckName, isPublished, isRejected, type StudioItem } from "./helpers";
import { qaReviewOf, updateInfoOf, wpGmtDate, wpPostLink, awaitingQaOf, type UpdateInfo } from "./quality";
import {
  CAL_STATE_CLASS,
  CAL_STATE_LABEL,
  formatCountdown,
  channelConnected,
  connectionName,
  correctionsOfPolicy,
  describeCorrections,
  gateOf,
  isNeedsFix,
  plainPublishError,
  policyChannelOf,
  publishAfterOf,
  useAgenticActions,
  useIsAdmin,
  useNow,
  usePublishPolicy,
  verifyOf,
  vetoStateOf,
  type CalState,
  type GateCheck,
} from "./agentic";

// ── Chips ───────────────────────────────────────────────────────────────────

export function CalStateIcon({ state, size = 12 }: { state: CalState; size?: number }) {
  switch (state) {
    case "verified": return <ShieldCheck size={size} aria-hidden />;
    case "published": return <CheckCircle2 size={size} aria-hidden />;
    case "pulled_back": return <Undo2 size={size} aria-hidden />;
    case "failed": return <XCircle size={size} aria-hidden />;
    case "held": return <Pause size={size} aria-hidden />;
    case "waiting": return <Clock size={size} aria-hidden />;
    case "skipped": return <CircleSlash size={size} aria-hidden />;
    case "ordered": return <Loader2 size={size} className="animate-spin" aria-hidden />;
    default: return null;
  }
}

export function CalStateChip({ state, label }: { state: CalState; label?: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${CAL_STATE_CLASS[state]}`}
      data-testid={`cal-state-${state}`}
    >
      <CalStateIcon state={state} />
      {label ?? CAL_STATE_LABEL[state]}
    </span>
  );
}

/** "9h 20m", ticking. Renders nothing when there is no time to count down to. */
export function Countdown({ until, prefix = "" }: { until: Date | null; prefix?: string }) {
  const now = useNow(30_000);
  if (!until) return null;
  return (
    <span data-testid="countdown">
      {prefix}
      {formatCountdown(until.getTime() - now)}
    </span>
  );
}

/** The extra chips agentic mode adds to a list row: veto status, a failed check, and the post-publish verdict. */
export function AgenticChips({ item }: { item: StudioItem }) {
  // A rejected piece is held by the server so it can't be scheduled later; to the owner it is simply rejected.
  const veto = isRejected(item) ? "none" : vetoStateOf(item);
  const gate = gateOf(item);
  const verify = verifyOf(item);
  const after = publishAfterOf(item);
  const published = isPublished(item);
  const update = updateInfoOf(item);
  const awaitingQa = awaitingQaOf(item);
  return (
    <>
      {update && (
        <Chip className="border-violet-500/40 bg-violet-500/15 text-violet-200" title="This rewrites a post that is already live, so what must stay the same is locked.">
          <RefreshCw size={11} className="mr-1" aria-hidden />
          {update.wpPostId ? `Update of #${update.wpPostId}` : "Update"}
        </Chip>
      )}
      {awaitingQa && (
        <Chip className="border-sky-500/40 bg-sky-500/15 text-sky-200" title="It passed the checks. A second reviewer is still reading it against its sources (usually under an hour).">
          <Clock size={11} className="mr-1" aria-hidden /> Waiting for independent QA
        </Chip>
      )}
      {veto === "pending" && !published && (
        <Chip className="border-amber-500/40 bg-amber-500/15 text-amber-200" title="It publishes by itself when the countdown ends, unless you hold it.">
          <Clock size={11} className="mr-1" aria-hidden />
          {after ? <Countdown until={after} prefix="Publishes in " /> : "Waiting to publish"}
        </Chip>
      )}
      {isNeedsFix(item) && !published && (
        <Chip className="border-orange-500/40 bg-orange-500/15 text-orange-200" title="It needs a fix before it can go out.">
          Needs a fix
        </Chip>
      )}
      {veto === "held" && !isNeedsFix(item) && (
        <Chip className="border-zinc-500/40 bg-zinc-500/20 text-zinc-300">
          <Pause size={11} className="mr-1" aria-hidden /> On hold
        </Chip>
      )}
      {veto === "released" && !published && (
        <Chip className="border-sky-500/30 bg-sky-500/15 text-sky-200" title="Released: it is being published now.">
          <Play size={11} className="mr-1" aria-hidden /> Publishing now
        </Chip>
      )}
      {gate && !gate.passed && !published && !isNeedsFix(item) && (
        <Chip className="border-red-500/30 bg-red-500/15 text-red-300" title="The pre-publish check found a problem.">
          <ShieldX size={11} className="mr-1" aria-hidden /> Check failed
        </Chip>
      )}
      {verify?.state === "verified" && (
        <Chip className="border-emerald-500/40 bg-emerald-500/20 text-emerald-100" title="We checked the live page after it went out.">
          <ShieldCheck size={11} className="mr-1" aria-hidden /> Verified
        </Chip>
      )}
      {verify?.state === "still_live" && (
        <Chip className="border-amber-500/40 bg-amber-500/15 text-amber-200" title={verify.reason ?? "The follow-up check found a problem."}>
          {verify.inconclusive ? "Couldn't verify" : "Check after publishing failed"}
        </Chip>
      )}
    </>
  );
}

// ── Hold / release ──────────────────────────────────────────────────────────

/**
 * The buttons for a piece that is waiting out its veto window, has been released, or is on hold:
 *   waiting -> "Hold" and "Release now";  released (not sent yet) -> "Hold";  held -> "Release".
 * Renders nothing for any other state, so callers can place it unconditionally.
 */
export function VetoControls({
  item,
  size = "default",
  onDone,
  testIdSuffix = "",
}: {
  item: StudioItem;
  size?: "sm" | "default";
  onDone?: () => void;
  testIdSuffix?: string;
}) {
  const { hold, release } = useAgenticActions();
  const isAdmin = useIsAdmin();
  const [confirm, setConfirm] = useState(false);
  const state = vetoStateOf(item);
  // A pulled-back piece must be edited (and re-checked) before it can go out, and a rejected piece is held on purpose: neither is released from here.
  if (state === "none" || isPublished(item) || isNeedsFix(item) || isRejected(item)) return null;
  const gate = gateOf(item);
  const busy = hold.isPending || release.isPending;
  const sfx = `${item.id}${testIdSuffix}`;
  // The server refuses a release until the pre-publish check has passed AND independent QA has come back, and only an owner or admin may release.
  const blocked = gate?.passed !== true ? "gate" : gate.awaitingQa ? "qa" : !isAdmin ? "role" : null;
  const blockedText =
    blocked === "gate"
      ? "Release is blocked until the pre-publish check passes. Edit it, then re-run the check."
      : blocked === "qa"
        ? "Release is blocked until independent QA finishes (usually under an hour)."
        : "Only the workspace owner or an admin can release.";
  const canRelease = state !== "released";
  const releaseButton = (
    <Button
      type="button"
      size={size}
      disabled={busy || blocked !== null}
      onClick={() => setConfirm(true)}
      className="bg-emerald-600 text-white hover:bg-emerald-600/90"
      data-testid={`button-release-${sfx}`}
    >
      {release.isPending ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
      {state === "pending" ? "Release now" : "Release"}
    </Button>
  );
  return (
    <>
      {(state === "pending" || state === "released") && (
        <Button type="button" variant="outline" size={size} disabled={busy} onClick={() => hold.mutate(item.id, { onSuccess: () => onDone?.() })} data-testid={`button-hold-${sfx}`}>
          {hold.isPending ? <Loader2 size={14} className="animate-spin" /> : <Hand size={14} />}
          Hold
        </Button>
      )}
      {canRelease && blocked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} className="inline-flex rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60" data-testid={`release-blocked-${sfx}`}>
              {releaseButton}
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{blockedText}</TooltipContent>
        </Tooltip>
      ) : canRelease ? (
        releaseButton
      ) : null}
      {canRelease && blocked && size === "default" && (
        <p className="w-full text-xs text-muted-foreground" data-testid="note-release-blocked">{blockedText}</p>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state === "pending" ? "Publish this now?" : "Release this to publish?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {state === "pending"
                ? "It goes out right away instead of waiting for the rest of the countdown."
                : "It goes out right away. It will not wait for another countdown."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Not yet</AlertDialogCancel>
            <AlertDialogAction onClick={() => release.mutate(item.id, { onSuccess: () => onDone?.() })} data-testid={`button-confirm-release-${sfx}`}>
              Publish now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Review header: status, veto, verify, errors ─────────────────────────────

function Box({ tone, icon, children, testId }: { tone: "amber" | "grey" | "green" | "red" | "sky" | "violet"; icon: ReactNode; children: ReactNode; testId: string }) {
  const cls = {
    violet: "border-violet-500/30 bg-violet-500/10 text-violet-100",
    amber: "border-amber-500/30 bg-amber-500/10 text-amber-100",
    grey: "border-zinc-500/30 bg-zinc-500/10 text-zinc-200",
    green: "border-emerald-500/30 bg-emerald-500/10 text-emerald-100",
    red: "border-red-500/30 bg-red-500/10 text-red-100",
    sky: "border-sky-500/30 bg-sky-500/10 text-sky-100",
  }[tone];
  return (
    <div className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${cls}`} role="status" data-testid={testId}>
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1 break-words">{children}</div>
    </div>
  );
}

/** What is happening to this piece right now, in words: countdown, hold, release, publish error, verification. */
export function PublishStatusPanel({ item, workspace }: { item: StudioItem; workspace: any }) {
  const veto = isRejected(item) ? "none" : vetoStateOf(item);
  const gate = gateOf(item);
  const verify = verifyOf(item);
  const published = isPublished(item);
  const after = publishAfterOf(item);
  const needsFix = isNeedsFix(item) && !published;
  const chan = policyChannelOf(item);
  const unconnectedName = chan && channelConnected(workspace, chan) === false ? connectionName(chan) : null;
  // A failed follow-up check already explains itself (its own box below); do not repeat the raw error.
  const err = !published && !verify ? plainPublishError(item, workspace) : null;
  const pipeline = !!(item.gateResult || item.planSlotId || veto !== "none");
  const update = updateInfoOf(item);
  const qaFailed = !published && !needsFix && !isRejected(item) && qaReviewOf(item)?.passed === false && !awaitingQaOf(item);

  const blocks: ReactNode[] = [];

  // An update rewrites a post that is already live: say so first, with what the server will not let it change.
  if (update) {
    blocks.push(
      <Box key="update" tone="violet" icon={<RefreshCw size={16} />} testId="status-update">
        <UpdateSummary info={update} workspace={workspace} />
      </Box>,
    );
  }
  if (qaFailed) {
    blocks.push(
      <Box key="qa-failed" tone="red" icon={<ShieldX size={16} />} testId="status-qa-failed">
        <p className="font-medium">Independent QA did not pass</p>
        <p className="text-xs text-red-100/90">A second reviewer found a problem the checks did not. Read the findings in the Quality review below, then edit or reject it.</p>
      </Box>,
    );
  }

  if (veto === "pending" && !published) {
    blocks.push(
      <Box key="veto" tone="amber" icon={<Clock size={16} />} testId="status-veto-pending">
        <p className="font-medium">
          {after ? (
            <>
              Publishes in <Countdown until={after} />
              <span className="font-normal text-amber-100/80"> ({fmtDateTime(after)})</span>
            </>
          ) : (
            "Waiting to publish"
          )}
        </p>
        <p className="text-xs text-amber-100/80">It goes out by itself when the countdown ends, unless you hold it.</p>
      </Box>,
    );
  }
  if (needsFix && !verify) {
    blocks.push(
      <Box key="needsfix" tone="red" icon={<ShieldX size={16} />} testId="status-needs-fix">
        <p className="font-medium">Needs a fix</p>
        <p className="text-xs text-red-100/90">Edit it to fix the problem. Saving your edit sends it back through the pre-publish check.</p>
      </Box>,
    );
  }
  if (veto === "held" && !published && !needsFix) {
    blocks.push(
      <Box key="held" tone="grey" icon={<Pause size={16} />} testId="status-veto-held">
        <p className="font-medium">On hold</p>
        <p className="text-xs text-zinc-300/90">It will not publish until you release it.</p>
      </Box>,
    );
  }
  if (veto === "released" && !published) {
    blocks.push(
      <Box key="released" tone="sky" icon={<Play size={16} />} testId="status-veto-released">
        <p className="font-medium">Released</p>
        <p className="text-xs text-sky-100/80">
          {unconnectedName
            ? `${unconnectedName} isn't connected, so it can't go out by itself. Connect it in Settings → Publishing, or copy the text and post it by hand.`
            : "It goes out within a few minutes. This page updates when it is live."}
        </p>
      </Box>,
    );
  }
  if (err) {
    blocks.push(
      <Box key="err" tone="red" icon={<XCircle size={16} />} testId="status-publish-error">
        <p className="font-medium">It didn't go out</p>
        <p className="text-xs text-red-100/90" title={err.raw}>
          {err.text}
          {err.fixHref && (
            <>
              {" "}
              <Link href={err.fixHref} className="underline underline-offset-2" data-testid="link-fix-connection">
                {err.fixLabel}
              </Link>
            </>
          )}
        </p>
      </Box>,
    );
  }
  if (gate && !gate.passed && !published && !needsFix) {
    const failing = gate.checks.filter((c) => !c.passed && !c.unverified && c.blocking !== false).map((c) => humanizeCheckName(c.name));
    const unmeasured = gate.checks.filter((c) => !c.passed && c.unverified).map((c) => humanizeCheckName(c.name));
    blocks.push(
      <Box key="gate" tone="red" icon={<ShieldX size={16} />} testId="status-gate-failed">
        <p className="font-medium">The pre-publish check didn't pass</p>
        <p className="text-xs text-red-100/90">
          {failing.length ? `Failed: ${failing.slice(0, 5).join(", ")}${failing.length > 5 ? ` and ${failing.length - 5} more` : ""}. ` : ""}
          {unmeasured.length ? `Couldn't be measured: ${unmeasured.slice(0, 5).join(", ")}${unmeasured.length > 5 ? ` and ${unmeasured.length - 5} more` : ""}. ` : ""}
          Edit it, then re-run the check from the Checks panel.
        </p>
      </Box>,
    );
  }
  if (verify) {
    if (verify.state === "verified") {
      blocks.push(
        <Box key="verify" tone="green" icon={<ShieldCheck size={16} />} testId="status-verified">
          <p className="font-medium">Verified</p>
          <p className="text-xs text-emerald-100/80">
            We checked the live page after it went out and it looks right.{verify.checkedAt ? ` (${fmtDateTime(verify.checkedAt)})` : ""}
          </p>
        </Box>,
      );
    } else if (verify.state === "pulled_back") {
      blocks.push(
        <Box key="verify" tone="red" icon={<Undo2 size={16} />} testId={verify.restored ? "status-restored" : "status-pulled-back"}>
          <p className="font-medium">{verify.restored ? "Pulled back, original restored" : "Pulled back"}</p>
          <p className="text-xs text-red-100/90">
            {verify.restored
              ? `The update went live, but the check afterwards found a problem, so post ${update?.wpPostId ? `#${update.wpPostId} ` : ""}was put back to the version it replaced.`
              : update
                ? `The update went live, but the check afterwards found a problem, so it was pulled back. Open post ${update.wpPostId ? `#${update.wpPostId}` : "on your site"} and check which version is live.`
                : "It went out, but the check afterwards found a problem, so it was taken down again."}
            {verify.reason ? <span className="block pt-0.5">Why: {verify.reason}</span> : null}
            <span className="block pt-0.5">Edit it to fix this; it then goes through the pre-publish check again.</span>
          </p>
        </Box>,
      );
    } else {
      blocks.push(
        <Box key="verify" tone="amber" icon={<ShieldX size={16} />} testId="status-verify-failed">
          <p className="font-medium">{verify.inconclusive ? "Published, but the live page couldn't be checked" : "The check after publishing found a problem"}</p>
          <p className="text-xs text-amber-100/90">
            {verify.inconclusive
              ? "The page could not be loaded, so nothing was taken down. Open it and check it yourself."
              : /could not (pull back|restore)/i.test(String(item.publishError || ""))
                ? update
                  ? `The update is live and could not be put back automatically. Restore the earlier version of post ${update.wpPostId ? `#${update.wpPostId}` : "on your site"} yourself, then fix this.`
                  : "It is still live and could not be taken down automatically. Unpublish it yourself, then fix it."
                : "It is still live. Read it again and decide what to do."}
            {verify.reason ? <span className="block pt-0.5">Why: {verify.reason}</span> : null}
          </p>
        </Box>,
      );
    }
  } else if (published && pipeline) {
    blocks.push(
      <Box key="verify" tone="grey" icon={<Clock size={16} />} testId="status-not-verified">
        <p className="text-xs text-zinc-300/90">Not verified yet. The team checks the live page shortly after it goes out.</p>
      </Box>,
    );
  }

  if (blocks.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="publish-status">
      {blocks}
    </div>
  );
}

/** "Updates existing post #123 · base from Sep 18, 2026, 2:03 PM · slug locked · disclaimers must stay at 2". */
export function UpdateSummary({ info, workspace }: { info: UpdateInfo; workspace: any }) {
  const { policy } = usePublishPolicy();
  const link = wpPostLink(workspace, info.wpPostId);
  const baseDate = wpGmtDate(info.baseModifiedGmt);
  const facts: string[] = [];
  if (info.baseModifiedGmt) facts.push(`base from ${baseDate ? fmtDateTime(baseDate) : info.baseModifiedGmt}`);
  if (info.baseSlug) facts.push(`slug locked (/${info.baseSlug.replace(/^\/+|\/+$/g, "")}/)`);
  if (info.baseDisclaimerCount !== null) facts.push(`disclaimers must stay at ${info.baseDisclaimerCount}`);
  return (
    <>
      {/* One sentence, as the owner reads it: "Updates existing post #123 · base from ... · slug locked · disclaimers must stay at 2" */}
      <p className="text-sm" data-testid="update-line">
        <span className="font-medium" data-testid="update-head">
          Updates existing post{" "}
          {info.wpPostId ? (
            link ? (
              <a href={link} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2" data-testid="link-update-post" title="Open the live post">
                #{info.wpPostId}
              </a>
            ) : (
              <>#{info.wpPostId}</>
            )
          ) : (
            info.target ?? "on your site"
          )}
        </span>
        {facts.length > 0 && (
          <span className="text-violet-100/85" data-testid="update-facts">
            {" · "}
            {facts.join(" · ")}
          </span>
        )}
      </p>
      {facts.length > 0 && <p className="text-xs text-violet-100/70">It replaces that post when it goes out, so the address and the disclaimers must come out the same.</p>}
      {info.isCorrection && (
        <p className="text-xs text-violet-100/80" data-testid="update-correction-note">
          A supervisor correction. It follows your corrections setting{policy ? `: ${describeCorrections(correctionsOfPolicy(policy))}` : ""}.
        </p>
      )}
    </>
  );
}

// ── One check, as a row ─────────────────────────────────────────────────────

/** passed, failed, a warning (advisory: never stops publishing) or not measured (the page could not be reached: neither a pass nor a failure of the piece). */
function CheckLine({ c }: { c: GateCheck }) {
  const kind = c.passed ? "pass" : c.unverified ? "unverified" : c.blocking === false ? "warn" : "fail";
  const icon = {
    pass: <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-400" aria-label="Passed" />,
    unverified: <CircleHelp size={15} className="mt-0.5 shrink-0 text-amber-400" aria-label="Not measured" />,
    warn: <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-400" aria-label="Warning" />,
    fail: <XCircle size={15} className="mt-0.5 shrink-0 text-red-400" aria-label="Failed" />,
  }[kind];
  const tone = { pass: "text-foreground/90", unverified: "text-amber-200", warn: "text-amber-200", fail: "text-red-200" }[kind];
  const suffix = { pass: "", unverified: " (not measured)", warn: " (warning only)", fail: "" }[kind];
  return (
    <li className="flex items-start gap-2 text-sm" data-ok={c.passed} data-kind={kind}>
      {icon}
      <span className="min-w-0 break-words">
        <span className={tone}>
          {humanizeCheckName(c.name)}
          {suffix}
        </span>
        {c.detail && <span className="block text-xs text-muted-foreground">{c.detail}</span>}
      </span>
    </li>
  );
}

function countChecks(checks: GateCheck[]): { failed: number; warned: number; unmeasured: number } {
  return {
    failed: checks.filter((c) => !c.passed && !c.unverified && c.blocking !== false).length,
    warned: checks.filter((c) => !c.passed && !c.unverified && c.blocking === false).length,
    unmeasured: checks.filter((c) => !c.passed && c.unverified).length,
  };
}

// ── After-publish check (inside the Checks panel) ───────────────────────────

/** Every check the server measured on the live page, with the value it measured. Failed ones first. */
export function VerifyBlock({ item }: { item: StudioItem }) {
  const verify = verifyOf(item);
  if (!verify || verify.checks.length === 0) return null;
  const { failed, warned, unmeasured } = countChecks(verify.checks);
  const label =
    verify.state === "verified"
      ? "Passed"
      : verify.inconclusive
        ? "Could not be checked"
        : verify.state === "pulled_back"
          ? verify.restored
            ? "Failed, original restored"
            : "Failed, pulled back"
          : "Failed, still live";
  const good = verify.state === "verified";
  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid="checks-verify" data-verify={verify.state}>
      <div className="flex flex-wrap items-center gap-2">
        {good ? <ShieldCheck size={15} className="text-emerald-400" /> : <ShieldX size={15} className="text-red-400" />}
        <h3 className="text-sm font-semibold text-foreground">After-publish check</h3>
        <span className={`text-xs font-medium ${good ? "text-emerald-300" : "text-red-300"}`} data-testid="verify-verdict">{label}</span>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="verify-counts">
        {verify.checkedAt && fmtDateTime(verify.checkedAt) ? `Measured on the live page ${fmtDateTime(verify.checkedAt)}. ` : "Measured on the live page. "}
        {verify.checks.length} checks, {failed} failed{warned ? `, ${warned} warning${warned === 1 ? "" : "s"}` : ""}{unmeasured ? `, ${unmeasured} not measured` : ""}.
      </p>
      <ul className="space-y-1.5" data-testid="verify-checks">
        {verify.checks.map((c, i) => (
          <CheckLine key={`${c.name}-${i}`} c={c} />
        ))}
      </ul>
    </div>
  );
}

// ── Gate checks (inside the Checks panel) ───────────────────────────────────

const GATE_SHOWN = 6;

/** The pre-publish check: each check with pass / fail, when it ran, and a button to run it again. */
export function GateBlock({ item }: { item: StudioItem }) {
  const { rerunGate } = useAgenticActions();
  const [all, setAll] = useState(false);
  const { policy } = usePublishPolicy();
  const gate = gateOf(item);
  const veto = vetoStateOf(item);
  // Only pieces that go through the pipeline show a check: planned, in a veto window, already checked, or on a channel that is not Manual.
  const ch = policyChannelOf(item);
  const onPolicyChannel = !!(policy && ch && policy[ch]?.mode !== "manual");
  if (!gate && !item.planSlotId && veto === "none" && !onPolicyChannel) return null;
  const rows = gate?.checks ?? [];
  const shown = all ? rows : rows.slice(0, GATE_SHOWN);
  const published = isPublished(item);
  return (
    <div className="space-y-2 border-t border-border pt-3" data-testid="checks-gate" data-gate={gate ? (gate.passed ? "passed" : "failed") : "none"}>
      <div className="flex flex-wrap items-center gap-2">
        {gate?.passed ? <ShieldCheck size={15} className="text-emerald-400" /> : gate ? <ShieldX size={15} className="text-red-400" /> : <ShieldCheck size={15} className="text-muted-foreground" />}
        <h3 className="text-sm font-semibold text-foreground">Pre-publish check</h3>
        <span className={`text-xs font-medium ${gate ? (gate.passed ? "text-emerald-300" : "text-red-300") : "text-muted-foreground"}`} data-testid="gate-verdict">
          {gate ? (gate.passed ? "Passed" : "Did not pass") : "Not run yet"}
        </span>
        {!published && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="ml-auto h-7 px-2 text-xs"
            disabled={rerunGate.isPending}
            onClick={() => rerunGate.mutate(item.id)}
            data-testid="button-rerun-gate"
          >
            {rerunGate.isPending ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {gate ? "Re-run check" : "Run check"}
          </Button>
        )}
      </div>
      {gate?.ranAt && fmtDateTime(gate.ranAt) && <p className="text-xs text-muted-foreground">Checked {fmtDateTime(gate.ranAt)}</p>}
      {gate?.passed && gate.awaitingQa && !published && (
        <p className="text-xs text-sky-300" data-testid="gate-awaiting-qa">These checks passed. Independent QA is still to come.</p>
      )}
      {rows.length > 0 && (
        <ul className="space-y-1.5" data-testid="gate-checks">
          {shown.map((c, i) => (
            <CheckLine key={`${c.name}-${i}`} c={c} />
          ))}
        </ul>
      )}
      {rows.length > GATE_SHOWN && (
        <button type="button" className="text-xs text-blue-400 underline underline-offset-2" onClick={() => setAll((v) => !v)} data-testid="button-gate-show-all">
          {all ? "Show fewer checks" : `Show all ${rows.length} checks`}
        </button>
      )}
      {!gate && <p className="text-xs text-muted-foreground">This piece hasn't been through the pre-publish check yet.</p>}
    </div>
  );
}
